import { useMemo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FiatWalletKycState } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Actions } from '#src/atoms/layout/Actions/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineL } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { fiatWallet, glossary } from '#src/lib/glossary.ts';

const Content = styled(Vertical).attrs({ gap: 0 })`
  gap: var(--triple-unit);
  padding: var(--double-unit);
`;

const Text = styled(Vertical).attrs({ gap: 0 })`
  gap: var(--double-unit);
  text-align: center;
  width: 100%;
`;

export enum InterstitialContextModalMode {
  DEPOSIT = 'deposit',
  REWARD = 'reward',
}

export type Props = {
  onAccept: () => void;
  onDecline: () => void;
  mode: InterstitialContextModalMode;
};

const underReviewMessages = defineMessages({
  title: {
    defaultMessage: 'We’re reviewing your ID',
  },
  description: {
    defaultMessage:
      'Once we validate your ID, you will have access to cash deposits, withdrawals, and rewards. We’ll let you know if we need more info from you.',
  },
});

const canListAndTradeTitles = defineMessages<InterstitialContextModalMode>({
  [InterstitialContextModalMode.DEPOSIT]: {
    defaultMessage: 'Enable cash deposit',
  },
  [InterstitialContextModalMode.REWARD]: {
    defaultMessage: 'Enable cash rewards',
  },
});

const canListAndTradeDescriptions =
  defineMessages<InterstitialContextModalMode>({
    [InterstitialContextModalMode.DEPOSIT]: {
      defaultMessage:
        'To enable cash deposits, please verify your identity by adding a government-issued ID.',
    },
    [InterstitialContextModalMode.REWARD]: {
      defaultMessage:
        'To receive cash rewards you must verify your identity by adding a government ID.',
    },
  });

const descriptions = defineMessages<InterstitialContextModalMode>({
  [InterstitialContextModalMode.DEPOSIT]: {
    defaultMessage:
      'To make a cash deposit, please activate your Cash Wallet and verify your identify by adding a government-issued ID.',
  },
  [InterstitialContextModalMode.REWARD]: {
    defaultMessage:
      'To receive cash rewards you must activate your cash Wallet and verify your identity by adding a government ID.',
  },
});

export const InterstitialContextModal = ({
  onAccept,
  onDecline,
  mode,
}: Props) => {
  const { canListAndTrade, kycStatus } = useFiatBalance();

  const underReview =
    kycStatus &&
    [
      FiatWalletKycState.VALIDATION_ASKED,
      FiatWalletKycState.CREATED,
    ].includesValue(kycStatus);

  const title = useMemo(() => {
    if (underReview) {
      return underReviewMessages.title;
    }

    return canListAndTrade
      ? canListAndTradeTitles[mode]
      : fiatWallet.activateCashWallet;
  }, [canListAndTrade, mode, underReview]);

  const description = useMemo(() => {
    if (underReview) {
      return underReviewMessages.description;
    }
    return canListAndTrade
      ? canListAndTradeDescriptions[mode]
      : descriptions[mode];
  }, [canListAndTrade, mode, underReview]);

  return (
    <Dialog
      hideHeader
      maxWidth="xs"
      onClose={onDecline}
      fullWidth
      fullScreen={false}
      open
    >
      <Content>
        <Text>
          <HeadlineL as="h3">
            <FormattedMessage {...title} />
          </HeadlineL>
          <BodyM>
            <FormattedMessage {...description} />
          </BodyM>
        </Text>
        <Actions direction="vertical" gap={1}>
          {underReview ? (
            <Button onClick={onDecline} color="primary" size="medium">
              <FormattedMessage {...glossary.gotIt} />
            </Button>
          ) : (
            <>
              <Button onClick={onAccept} color="primary" size="medium">
                <FormattedMessage
                  {...(canListAndTrade
                    ? fiatWallet.addMyId
                    : fiatWallet.activateCashWalletAndAddAnId)}
                />
              </Button>
              <Button onClick={onDecline} color="tertiary" size="medium">
                <FormattedMessage {...glossary.back} />
              </Button>
            </>
          )}
        </Actions>
      </Content>
    </Dialog>
  );
};
